import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { RemixContainer } from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expVal } from '@confluence/feature-experiments';

import type { DynamicUiSource } from './common-types.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

type RendererRemixCreatorProps = {
	// TODO: Not sure what this will look like yet, as renderer selection
	// still needs to be implemented.
	selectedContext: string;
	onClose: () => void;
};

const RendererRemixModal = ({ selectedContext, onClose }: RendererRemixCreatorProps) => {
	const { sendAIFCPrompt, openAIFCStagingModal } = useRemixToAIFC();

	const handleOptionSelect = useCallback(
		async (type: string) => {
			// TOOD: This should be created from selectedContext
			const dynamicUiSource = {} as DynamicUiSource;
			await sendAIFCPrompt(selectedContext, type, dynamicUiSource);
			openAIFCStagingModal();
			onClose();
		},
		[onClose, sendAIFCPrompt, openAIFCStagingModal, selectedContext],
	);

	return <RemixContainer selectedText="" onClose={onClose} onOptionSelect={handleOptionSelect} />;
};

type RendererRemixContextProps = {
	openRemixModal: (selection: string) => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
});

export const useRendererRemixContext = () => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({ children }: { children: React.ReactNode }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedContext, setSelectedContext] = useState<string | undefined>(undefined);

	const openModal = useCallback((selection: string) => {
		setSelectedContext(selection);
		setIsModalOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setSelectedContext(undefined);
		setIsModalOpen(false);
	}, []);

	const context = useMemo(() => {
		return { openRemixModal: openModal };
	}, [openModal]);

	return (
		<RendererRemixContext.Provider value={context}>
			{children}
			{isModalOpen && selectedContext && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal selectedContext={selectedContext} onClose={closeModal} />
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

export const RendererRemixContainer = ({ children }: { children: React.ReactNode }) => {
	if (!expVal('cc-maui-experiment', 'isEnabled', false)) {
		return <>{children}</>;
	}

	return <RendererRemixContainerImpl>{children}</RendererRemixContainerImpl>;
};
